/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useHistory } from 'react-router-dom'
import { Formik } from 'formik'
import clsx from 'clsx'
import pluralize from 'pluralize'

import {
  Button,
  usePaginatedProjects,
  useUtils,
  useGlobalState,
  useMessage,
  Modal,
  DomainSelector,
} from '@pepper/ui'
import { createProject } from '@pepper/utils/src/api/projects/projects'

import { TextField } from 'components/FormFields/FormFields'
import ProjectPicker from 'components/SelectProjectModal/ProjectPicker'
import VerticalSelector from 'components/CreateContentModal/VerticalSelector'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { createPrivateDocument } from 'utils/api/item'
import {
  FormValues,
  generateDraftData,
  initialValues,
  validationSchema,
} from './CreateItemModal.utils'

import s from './CreateItemModal.module.css'
import { Icon } from '@iconify/react'

type Item = {
  title: string
  content_gap_report_id: string
  word_count: string | number
}

type Props = {
  title?: string
  items: Partial<Item>[]
  type: 'assignment' | 'private_doc'
  defaultValues?: Partial<FormValues>
  // adding custom react node to the start of the form body
  preFormBody?: React.ReactNode
}

const PAGE_SIZE = 10

function CreateItemModal({
  title = 'Order from Expert Talent',
  items,
  type,
  defaultValues,
  preFormBody,
}: Props) {
  const modal = useModal()
  const history = useHistory()
  const companyId = useSelectedCompanyId()
  const { verticals } = useUtils()
  const { setPendingSidebarCountKeys } = useGlobalState()
  const { showError } = useMessage()

  const [isCreatingDraft, setIsCreatingDraft] = useState(false)
  const [isCreatingItem, setIsCreatingItem] = useState(false)

  const { projects, refetchProjects } = usePaginatedProjects({
    company_id: companyId,
    status: [
      'completed',
      'editing_progress',
      'fresh',
      'writing_progress',
      'closed',
    ],
    page_size: PAGE_SIZE,
    pagination_enabled: false,
  })

  const handleCreateDoc = async (
    projectId: string,
    _items: Partial<Item>[]
  ) => {
    const responses = await Promise.allSettled<{ item_id: string }>(
      _items.map(item =>
        createPrivateDocument({
          projectId,
          title: item.title,
          gapReportId: item.content_gap_report_id,
        }).then(response => {
          if (response.status === 'ok') {
            return { item_id: response.data?.item_id }
          }
          throw new Error(response.error)
        })
      )
    )

    const requestFailed = responses.some(result => result.status === 'rejected')
    if (requestFailed) {
      return showError('Unable to create document(s). Please try again')
    }

    modal.remove()
    const newlyCreatedItems = responses.map(response =>
      response.status === 'fulfilled' ? response.value.item_id : ''
    )

    // open document in new tab if only one document is created else open project listing page
    if (newlyCreatedItems.length === 1) {
      const url = `/${companyId}/items/${newlyCreatedItems[0]}?project_id=${projectId}`
      window.open(url, '_blank')
    } else {
      history.push(`/project/${projectId}/items`, {
        newItems: newlyCreatedItems,
      })
    }

    setIsCreatingItem(false)
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsCreatingDraft(true)
      const {
        draftData,
        projectId: _projectId,
        error: _error = '',
      } = await generateDraftData({
        values,
        projects,
        items: items.map(item => ({
          assignment_title: item.title,
          content_gap_report_id: item.content_gap_report_id,
          word_count: item.word_count,
        })),
        verticals,
        companyId,
      })
      setPendingSidebarCountKeys(['allProjectsCount'])
      if (!draftData || !_projectId) {
        showError(_error || 'Unable to create Project')
        return
      }
      refetchProjects()
      // create draft in case of assignment
      if (type === 'assignment') {
        const response = await createProject({
          body: draftData,
          type: 'draft',
        })
        if (response.status !== 'ok') {
          showError(response.error)
        }

        history.push(
          `/create-project-flow?draft_id=${response.data.id}&projectId=${_projectId}`
        )
        modal.remove()
      }

      if (type === 'private_doc') {
        setIsCreatingItem(true)
        handleCreateDoc(_projectId, items)
      }
    } catch (error) {
      showError(error)
    } finally {
      setIsCreatingDraft(false)
    }
  }

  return (
    <Formik<FormValues>
      initialValues={{ ...initialValues, ...defaultValues }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {props => {
        const showCreateProject = props.values.projectId === 'new'
        return (
          <Modal
            visible={modal.visible}
            onCancel={modal.remove}
            size={showCreateProject ? 'sm' : 'lg'}
            footer={null}
          >
            <div className={s.modalTitle}>{title}</div>

            <div className={s.modalBody}>
              {preFormBody}
              <div className={s.contentModalBody}>
                {!showCreateProject && (
                  <>
                    <div
                      className={clsx(s.selectField, s.inputV2Wrap)}
                      data-cy="projectId"
                    >
                      <ProjectPicker
                        onProjectClick={id => {
                          props.setFieldValue('projectId', id)
                        }}
                        onCreateNew={() => {
                          props.setFieldValue('projectId', 'new')
                        }}
                        listHeightPx={300}
                      />
                    </div>
                    {type === 'assignment' && (
                      <VerticalSelector
                        formikProps={props}
                        defaultValue={() => props.values.verticalId}
                        className={s.inputV2Wrap}
                      />
                    )}
                  </>
                )}
                {showCreateProject && (
                  <div>
                    <TextField
                      name="projectName"
                      label="Project Name"
                      required={true}
                      className={s.inputV2Wrap}
                      fieldProps={{
                        placeholder: 'Enter the new project name',
                        loadV2: true,
                      }}
                    />
                    {type === 'assignment' && (
                      <VerticalSelector
                        formikProps={props}
                        defaultValue={() => props.values.verticalId}
                        className={s.inputV2Wrap}
                      />
                    )}
                    <div className={s.inputV2Wrap}>
                      <div className={s.label}>
                        Project Domain<span className={s.required}>*</span>
                      </div>
                      <DomainSelector
                        companyId={companyId}
                        isControlled={true}
                        size="regular"
                        onChange={domainId =>
                          props.setFieldValue('domainId', domainId)
                        }
                        value={props.values.domainId}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={s.modalFooter}>
              <div className={s.left}>
                {showCreateProject && (
                  <button
                    className={s.backBtn}
                    type="button"
                    onClick={() => {
                      props.setFieldValue('projectId', '')
                    }}
                  >
                    <Icon icon="lucide:chevron-left" width={16} height={16} />
                    Select Project
                  </button>
                )}
              </div>

              <div className={s.right}>
                <Button
                  type="link"
                  onClick={modal.remove}
                  disabled={isCreatingDraft}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={props.submitForm}
                  disabled={!props.dirty || !props.isValid}
                  loading={isCreatingDraft || isCreatingItem}
                >
                  Create {items.length > 1 && items.length.toString()}{' '}
                  {pluralize('Item', items.length)}
                </Button>
              </div>
            </div>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default NiceModal.create<Props>(CreateItemModal)
